import Chrysler from '@carvana/ds-icons/Chrysler';
import Jeep from '@carvana/ds-icons/Jeep';
import Ram from '@carvana/ds-icons/Ram';
import RamHorizontal from '@carvana/ds-icons/RamHorizontal';
import Link from '@carvana/ds-link';
import { icon } from '@carvana/ds-styles';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import cx from 'classnames';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useIsMobile } from '~/context/common/DeviceContext';
import DodgeIcon from '~/icons/new-car/DodgeIcon';
import { type NewCarVariant } from '~/utilities/srp/new-car/getNewCarVariant';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import { Wrapper } from '../PromoBanner.styles';

type OwnProps = {
  bannerName: string;
  href: string;
  make: string;
  variant: NewCarVariant;
};

// DS icons hardcode their own path fill and ignore the color prop, so override it via CSS.
const FILL_OVERRIDE_CLASSES = '[&_path]:fill-[var(--icon-fill)]';
const fillOverrideStyle = {
  '--icon-fill': icon.inverse
} as React.CSSProperties;

const makeIcons: Record<string, typeof Chrysler> = {
  chrysler: Chrysler,
  jeep: Jeep,
  ram: RamHorizontal
};

const makeMobileIcons: Record<string, typeof Chrysler> = {
  ram: Ram
};

const makeLogoStyles: Record<string, string> = {
  chrysler:
    '-mb-md sm:mb-0 h-auto w-[80px] sm:w-[150px] lg:w-[190px] mr-xl sm:mr-0',
  dodge:
    'mb-xl sm:mb-0 h-auto w-[65px] sm:w-[150px] lg:w-[190px] mr-xl sm:mr-0',
  jeep: 'h-auto w-[70px] sm:w-[90px] lg:w-[115px] mr-xl sm:mr-0',
  ram: 'h-auto mb-xl sm:mb-0 w-[55px] sm:w-[150px] lg:w-[190px] mr-xl sm:mr-0'
};

const makeContainerStyles: Record<string, string> = {
  chrysler: 'pr-0',
  dodge: 'pr-0'
};

const defaultLogoStyle = 'w-[56px] sm:w-[100px] lg:w-[150px]';

const backgroundDesktopSrc = `${appAssetsCdnUrl}/images/new-car/hero-make-background-large.svg`;
const backgroundDesktopXlSrc = `${appAssetsCdnUrl}/images/helios/promoBanners/ncc/make/background-desktop.svg`;
const backgroundMobileSrc = `${appAssetsCdnUrl}/images/helios/promoBanners/ncc/make/background-mobile.svg`;

function BrandLogo({
  make,
  normalizedMake
}: {
  make: string;
  normalizedMake: string;
}) {
  const isMobile = useIsMobile();

  const className = cx(
    'block object-contain',
    makeLogoStyles[normalizedMake] ?? defaultLogoStyle,
    FILL_OVERRIDE_CLASSES
  );

  if (normalizedMake === 'dodge') {
    return (
      <DodgeIcon
        arrowUsesFill
        aria-label="Dodge logo"
        className={className}
        color={icon.inverse}
        data-testid="make-logo"
        role="img"
        style={fillOverrideStyle}
        orientation={isMobile ? 'vertical' : 'horizontal'}
      />
    );
  }

  const Icon = isMobile
    ? makeMobileIcons[normalizedMake] ?? makeIcons[normalizedMake]
    : makeIcons[normalizedMake];

  if (!Icon) {
    return null;
  }

  return (
    <Icon
      aria-label={`${make} logo`}
      className={className}
      data-testid="make-logo"
      role="img"
      style={fillOverrideStyle}
    />
  );
}

export default function MakeBanner({
  bannerName,
  href,
  make,
  variant
}: OwnProps) {
  const normalizedMake = toLowerKebabCase(make);
  const shopAnalyticsAttributes = JSON.stringify({
    category: bannerName,
    label: 'Select Color, Options & More',
    variant
  });

  return (
    <Wrapper className="bg-background-strong">
      <picture aria-hidden="true">
        <source
          media={`(min-width: ${breakpoints.xl}px)`}
          srcSet={backgroundDesktopXlSrc}
        />
        <source
          media={`(min-width: ${breakpoints.sm}px)`}
          srcSet={backgroundDesktopSrc}
        />
        <img
          alt=""
          className="absolute inset-0 size-full object-cover"
          src={backgroundMobileSrc}
        />
      </picture>

      <div className="relative z-1 flex flex-1 flex-col justify-center gap-lg py-2xl pl-2xl sm:pr-0 lg:py-4xl lg:pl-4xl">
        <h4 className="t-heading-sm max-w-[185px] text-text-inverse sm:t-heading-xl sm:max-w-[350px] md:max-w-[290px] lg:t-heading-2xl lg:max-w-full">
          Build your perfect new{' '}
          <span className="capitalize text-yellow-400">{make}</span>
        </h4>
        <p className="hidden max-w-[348px] t-body-sm text-text-inverse sm:block lg:max-w-[450px] xl:max-w-full">
          All brand new models — pickup or delivery as soon as today!
        </p>
        <div>
          <Link
            className="text-text-inverse"
            data-adobe-id="select-color-options-more"
            data-analytics-attributes={shopAnalyticsAttributes}
            data-analytics-id="Banner"
            data-testid="promo-banner-select-color-options-more"
            href={href}
            size="sm"
          >
            Select Color, Options & More
          </Link>
        </div>
      </div>

      <div
        className={cx(
          'relative z-1 flex sm:w-2/5 items-end sm:items-center justify-center',
          makeContainerStyles[normalizedMake]
        )}
      >
        <BrandLogo make={make} normalizedMake={normalizedMake} />
      </div>
    </Wrapper>
  );
}
